// /app/frontend/src/pages/compliance/vendor/SimplifiedVendorWorkflow.jsx
// Simplified Vendor Management - Caribbean Focus (Due Diligence is separate)

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import { Progress } from '../../../components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { vendorV2API, entitiesAPI, dueDiligenceAPI, vendorRiskAssessmentAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  ArrowLeft, ArrowRight, Save, Building2, User, Shield, FileText,
  CheckCircle, XCircle, AlertTriangle, Loader2, ClipboardCheck, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';
import { DatePicker } from '../../../components/ui/date-picker';
import {
  CARIBBEAN_COUNTRIES,
  RISK_TIERS,
  LIFECYCLE_STAGES,
  COMPANY_SIZES,
  DATA_CATEGORIES,
  DATA_SUBJECTS,
  RISK_ASSESSMENT_QUESTIONS,
  RISK_TIER_RECOMMENDATIONS,
} from './constants-simplified';

// Tab configuration - 3 tabs only (Due Diligence is separate)
const VENDOR_TABS = [
  { id: 'vendor-info', label: 'Vendor Info', icon: Building2 },
  { id: 'contract', label: 'Contract', icon: FileText },
  { id: 'risk-review', label: 'Risk & Review', icon: Shield },
];

// Initial form data - aligned with backend VendorV2 model (Mar 5, 2026 - Option A)
const VENDOR_INITIAL_DATA = {
  entity_id: '',
  vendor_name: '',
  trading_name: '',
  registration_number: '', // Backend field: company registration number
  country_of_registration: 'JM', // Backend field: country where vendor registered (compliance)
  approximate_size: '', // Backend field: company size (Small, Medium, Large, Enterprise)
  vendor_type: 'processor', // Backend field: processor, controller, joint_controller
  service_category: '', // Backend field: required
  services_provided: '', // Backend field: detailed description of services
  processing_location: '',
  criticality: 'medium', // Backend field: low, medium, high, critical
  notes: '',
  lifecycle_stage: 'prospect',
  // risk_tier is NOT part of the VendorV2 model — tier is sourced from the separate
  // vendor_risk_assessments collection (see riskTierResult below)
  contacts: [{ name: '', role: '', email: '', phone: '', is_primary: true }], // Backend field: contacts array
  contract_info: {
    // Aligned with backend VendorContract model
    has_dpa: false,
    dpa_signed_date: '',
    dpa_expiry_date: '',
    contract_start_date: '',
    contract_end_date: '',
    auto_renewal: false,
    termination_notice_days: 30,
    clause_checklist: {},
    dpa_adequacy_score: 0.0,
  },
  // Risk Assessment - Question-based approach
  riskAssessmentAnswers: {
    // 7 questions - hidden scoring (Low=1, Medium=2, High=3, Critical=4)
    // Q1: Data type, Q2: Data volume, Q3: Processing location, Q4: DPA status
    // Q5: Security measures, Q6: Breaches/incidents, Q7: Criticality
    q1: null, // Selected option text
    q2: null,
    q3: null,
    q4: null,
    q5: null,
    q6: null,
    q7: null,
  },
  riskAssessmentDate: null, // When assessment was last completed
  riskTierResult: null, // Calculated result: { tier, recommendation, riskDrivers, nextReviewDate }
  // Data processing information
  data_categories_processed: [], // List of data categories (e.g., personal data, special categories)
  data_subjects_affected: [], // List of data subject types (e.g., employees, customers, patients)
  // Removed: risk_assessment (not in backend VendorV2 model)
  // Note: certifications are NOT part of vendor creation - they belong in Due Diligence Assessment
};

// Yes/No Button Group
function YesNoButtons({ value, onChange, testIdPrefix }) {
  return (
    <div className="flex gap-2">
      <Button
        type="button"
        size="sm"
        variant={value === true ? "default" : "outline"}
        onClick={() => onChange(true)}
        data-testid={`${testIdPrefix}-yes`}
        className={value === true ? "bg-green-600 hover:bg-green-700" : ""}
      >
        <CheckCircle className="h-4 w-4 mr-1" />
        Yes
      </Button>
      <Button
        type="button"
        size="sm"
        variant={value === false ? "default" : "outline"}
        onClick={() => onChange(false)}
        data-testid={`${testIdPrefix}-no`}
        className={value === false ? "bg-red-600 hover:bg-red-700" : ""}
      >
        <XCircle className="h-4 w-4 mr-1" />
        No
      </Button>
    </div>
  );
}

export default function SimplifiedVendorWorkflow() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { isDPO, user, isComplianceReadOnly: isReadOnly } = useAuth();
  const { theme } = useTheme();
  const isEditing = !!id;

  const [formData, setFormData] = useState(VENDOR_INITIAL_DATA);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
  const [activeTab, setActiveTab] = useState('vendor-info');
  const [recordId, setRecordId] = useState(id || null);
  const [vendorReference, setVendorReference] = useState('');
  const [latestDD, setLatestDD] = useState(null);
  const [assessmentHistory, setAssessmentHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const lastSavedData = useRef(null);
  const autoSaveTimer = useRef(null);

  // Calculate progress
  const calculateProgress = useCallback(() => {
    let total = 0;
    let completed = 0;

    // Vendor Info (5 required fields)
    total += 5;
    if (formData.entity_id || !isDPO) completed++;
    if (formData.vendor_name) completed++;
    if (formData.services_provided) completed++;
    if (formData.vendor_type) completed++;
    if (formData.service_category) completed++;

    // Contract (1 field)
    total += 1;
    if (formData.contract_info.has_dpa) completed++;

    // Risk Assessment (1 field) - now checking if all 7 questions answered
    total += 1;
    if (formData.riskAssessmentAnswers) {
      const answered = Object.values(formData.riskAssessmentAnswers).filter(a => a !== null).length;
      if (answered === 7) completed++;
    }

    return Math.round((completed / total) * 100);
  }, [formData, isDPO]);

  const progressPercentage = calculateProgress();

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        if (isDPO) {
          const entitiesRes = await entitiesAPI.getAll();
          setEntities(entitiesRes.data);
        }

        if (isEditing) {
          const vendorRes = await vendorV2API.getOne(id);
          const vendor = vendorRes.data;
          setFormData({
            ...VENDOR_INITIAL_DATA,
            ...vendor,
            contacts: vendor.contacts && vendor.contacts.length > 0 ? vendor.contacts : VENDOR_INITIAL_DATA.contacts,
            contract_info: { ...VENDOR_INITIAL_DATA.contract_info, ...vendor.contract_info },
            // risk_tier is now top-level field (from vendor directly)
            // No need for risk_assessment mapping anymore
          });
          setVendorReference(vendor.vendor_reference || '');
          // Snapshot only the profile fields (never assessment answers) so the auto-save
          // comparator matches what performAutoSave will send (Bug #11).
          const { riskAssessmentAnswers: _a, riskAssessmentDate: _d, riskTierResult: _r,
                  risk_assessment: _ra, days_until_expiry: _du, entity_name: _en,
                  ...profileOnly } = {
            ...VENDOR_INITIAL_DATA,
            ...vendor,
            contacts: vendor.contacts && vendor.contacts.length > 0 ? vendor.contacts : VENDOR_INITIAL_DATA.contacts,
            contract_info: { ...VENDOR_INITIAL_DATA.contract_info, ...vendor.contract_info },
          };
          profileOnly.dpo_org_id = user?.dpo_org_id || user?.entity_id;
          lastSavedData.current = JSON.stringify(profileOnly);

          // Fetch latest due diligence
          try {
            const ddRes = await dueDiligenceAPI.getVendorLatest(id);
            if (ddRes.data.has_assessment) {
              setLatestDD(ddRes.data);
            }
          } catch (e) {
            // No DD yet
          }

          // Fetch latest risk assessment and history
          try {
            const raRes = await vendorRiskAssessmentAPI.getLatest(id);
            if (raRes.data) {
              // Restore question answers from saved assessment
              const answersMap = {};
              if (raRes.data.answers && Array.isArray(raRes.data.answers)) {
                raRes.data.answers.forEach(answer => {
                  answersMap[answer.question_id] = answer.option_value;
                });
              }

              // Populate risk assessment fields from saved assessment
              setFormData(prev => ({
                ...prev,
                riskAssessmentAnswers: answersMap,
                riskTierResult: {
                  tier: raRes.data.risk_tier,
                  averageScore: raRes.data.average_score,
                  recommendation: raRes.data.recommended_action,
                  riskDrivers: raRes.data.risk_drivers,
                  nextReviewDate: raRes.data.next_review_date,
                  assessmentId: raRes.data.id,
                },
                riskAssessmentDate: new Date(raRes.data.assessed_at).toLocaleDateString(),
              }));
            }

            // Fetch assessment history
            const historyRes = await vendorRiskAssessmentAPI.getHistory(id, 10);
            if (historyRes.data && Array.isArray(historyRes.data)) {
              setAssessmentHistory(historyRes.data);
            }
          } catch (e) {
            // No risk assessment yet - that's fine
            console.log('No risk assessment found for this vendor');
          }
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEditing, isDPO]);

  // Auto-save
  const performAutoSave = useCallback(async () => {
    if (!formData.vendor_name) return;
    if (!formData.entity_id && isDPO) return;

    // Only compare the vendor-profile portion — exclude assessment fields so that
    // answering risk questions does NOT trigger a redundant PUT /vendors (Bug #11).
    // Also strip response-only enrichment fields so we never echo them back.
    const {
      riskAssessmentAnswers, riskAssessmentDate, riskTierResult,
      risk_assessment, days_until_expiry, entity_name,
      ...submitData
    } = formData;
    submitData.entity_id = submitData.entity_id || user?.entity_id;
    submitData.dpo_org_id = user?.dpo_org_id || user?.entity_id;

    const currentData = JSON.stringify(submitData);
    if (currentData === lastSavedData.current) return;

    try {
      setAutoSaveStatus('saving');

      if (recordId) {
        await vendorV2API.update(recordId, submitData);
      } else {
        const res = await vendorV2API.create(submitData);
        setRecordId(res.data.id);
        setVendorReference(res.data.vendor_reference);
        window.history.replaceState(null, '', `/compliance/vendors/simplified/${res.data.id}`);
      }

      lastSavedData.current = currentData;
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Auto-save failed:', error);
      toast.error(`Failed to save: ${error.response?.data?.detail || error.message}`);
      setAutoSaveStatus('idle');
    }
  }, [formData, recordId, isDPO, user]);

  useEffect(() => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(performAutoSave, 2000);
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [formData, performAutoSave]);

  // Handle field changes
  const handleChange = (field, value) => {
    console.log(`Updating ${field} to:`, value);
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedChange = (parent, fieldOrIndex, fieldNameOrValue, valueOrUndefined) => {
    // Support both:
    // handleNestedChange('contract_info', 'field', value)
    // handleNestedChange('contacts', 0, 'field', value) for arrays
    const isArrayAccess = typeof fieldOrIndex === 'number';

    if (isArrayAccess) {
      // Array access: (parent, index, field, value)
      const [arrayParent, index, field, value] = [parent, fieldOrIndex, fieldNameOrValue, valueOrUndefined];
      setFormData(prev => ({
        ...prev,
        [arrayParent]: prev[arrayParent].map((item, i) =>
          i === index ? { ...item, [field]: value } : item
        )
      }));
    } else {
      // Object access: (parent, field, value)
      const [objectParent, field, value] = [parent, fieldOrIndex, fieldNameOrValue];
      setFormData(prev => ({
        ...prev,
        [objectParent]: { ...prev[objectParent], [field]: value }
      }));
    }
  };


  // Navigation
  const goToNextTab = () => {
    const currentIndex = VENDOR_TABS.findIndex(t => t.id === activeTab);
    if (currentIndex < VENDOR_TABS.length - 1) {
      setActiveTab(VENDOR_TABS[currentIndex + 1].id);
    }
  };

  const goToPrevTab = () => {
    const currentIndex = VENDOR_TABS.findIndex(t => t.id === activeTab);
    if (currentIndex > 0) {
      setActiveTab(VENDOR_TABS[currentIndex - 1].id);
    }
  };

  // Final save
  const handleSave = async (asDraft = false) => {
    try {
      setSaving(true);

      // Filter out assessment-only fields AND fields that are response-only enrichment
      // (risk_assessment / days_until_expiry / entity_name) so we don't send them back.
      const {
        riskAssessmentAnswers, riskAssessmentDate, riskTierResult,
        risk_assessment, days_until_expiry, entity_name,
        ...submitData
      } = formData;
      submitData.entity_id = submitData.entity_id || user?.entity_id;
      submitData.dpo_org_id = user?.dpo_org_id || user?.entity_id;

      if (recordId) {
        await vendorV2API.update(recordId, submitData);
        toast.success('Vendor saved successfully');
      } else {
        const res = await vendorV2API.create(submitData);
        setRecordId(res.data.id);
        toast.success('Vendor created successfully');
      }

      if (!asDraft) {
        navigate('/compliance/vendors');
      }
    } catch (error) {
      console.error('Save failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const getRiskTierColor = (tier) => {
    const colors = {
      'not_assessed': 'bg-gray-500',
      'low': 'bg-green-500',
      'medium': 'bg-yellow-500',
      'high': 'bg-red-500',
      'critical': 'bg-red-700',
    };
    return colors[tier] || 'bg-gray-500';
  };

  // Calculate risk tier from 7 question answers
  const calculateRiskAssessment = useCallback((answers) => {
    // Check if all 7 questions answered
    const answeredQuestions = Object.values(answers).filter(a => a !== null);
    if (answeredQuestions.length < 7) return null;

    // Get scores from selected options
    const scores = [];
    RISK_ASSESSMENT_QUESTIONS.forEach((question) => {
      const selectedOption = question.options.find(opt => opt.value === answers[question.id]);
      if (selectedOption) {
        scores.push(selectedOption.score);
      }
    });

    // Calculate average score
    const averageScore = scores.reduce((a, b) => a + b, 0) / scores.length;

    // Check override rule: if any answer is Critical (4), tier must be at least High
    const hasMaxScore = scores.includes(4);

    // Determine tier
    let tier = 'low';
    if (averageScore >= 3.3) tier = 'critical';
    else if (averageScore >= 2.6) tier = 'high';
    else if (averageScore >= 1.6) tier = 'medium';
    else tier = 'low';

    // Apply override: if any question is critical, min tier is high
    if (hasMaxScore && tier === 'medium') tier = 'high';
    if (hasMaxScore && tier === 'low') tier = 'high';

    // Get recommendation
    const rec = RISK_TIER_RECOMMENDATIONS[tier];

    // Extract risk drivers (questions with High=3 or Critical=4 scores)
    const riskDrivers = [];
    const driverMessages = {
      q1_special: 'Vendor processes special category data — enhanced safeguards needed',
      q1_children: 'Vendor processes children\'s data — strict protections required',
      q2_50k: 'Large volume of data processed (50,000+ people) — increased exposure',
      q3_other: 'Data transferred to unknown/risky international locations',
      q4_noformal: 'No data processing agreement in place — action required',
      q4_unknown: 'Data processing agreement status unknown — clarification needed',
      q5_claims: 'Vendor security measures unverified — request documentation',
      q5_unknown: 'Vendor security measures unknown — request documentation',
      q6_significant: 'Significant security incident in past 2 years — review mitigations',
      q6_multiple: 'Multiple incidents or ongoing security concerns — escalate immediately',
      q7_critical: 'Critical to operations — high business impact if compromised',
      q7_soleprovider: 'Sole provider with no alternative — high dependency risk',
    };

    RISK_ASSESSMENT_QUESTIONS.forEach((question) => {
      const selectedOption = question.options.find(opt => opt.value === answers[question.id]);
      if (selectedOption && selectedOption.score >= 3) {
        const key = `${question.id}_${selectedOption.value}`;
        if (driverMessages[key]) {
          riskDrivers.push(driverMessages[key]);
        }
      }
    });

    // Calculate next review date
    const nextReviewDate = new Date();
    if (tier === 'low') nextReviewDate.setFullYear(nextReviewDate.getFullYear() + 1);
    else if (tier === 'medium') nextReviewDate.setMonth(nextReviewDate.getMonth() + 6);
    else if (tier === 'high') nextReviewDate.setMonth(nextReviewDate.getMonth() + 3);
    else nextReviewDate.setMonth(nextReviewDate.getMonth() + 1); // critical: 1 month

    return {
      tier,
      averageScore: averageScore.toFixed(2),
      recommendation: rec.recommendation,
      riskDrivers: riskDrivers.length > 0 ? riskDrivers : ['No critical risk drivers identified.'],
      nextReviewDate: nextReviewDate.toLocaleDateString(),
    };
  }, []);

  // Clear answers and start fresh assessment (reassess)
  const handleReassess = useCallback(() => {
    handleChange('riskAssessmentAnswers', {
      q1: null, q2: null, q3: null, q4: null, q5: null, q6: null, q7: null,
    });
    handleChange('riskTierResult', null);
    handleChange('riskAssessmentDate', null);
    toast.info('Assessment cleared. Please answer the questions again.');
  }, [handleChange]);

  // Save risk assessment to backend
  const saveRiskAssessment = useCallback(async (answers, result) => {
    if (!recordId) {
      console.warn('Cannot save assessment without vendor ID');
      return;
    }

    try {
      // Convert answers object to array format for backend
      const answerArray = RISK_ASSESSMENT_QUESTIONS.map((question) => {
        const selectedOption = question.options.find(opt => opt.value === answers[question.id]);
        return {
          question_id: question.id,
          question_text: question.question,
          selected_option: selectedOption?.label || '',
          option_value: selectedOption?.value || '',
          score: selectedOption?.score || 0,
        };
      });

      // Create assessment payload
      const assessmentPayload = {
        vendor_id: recordId,
        dpo_org_id: user?.dpo_org_id || user?.entity_id,
        assessed_by: user?.id || user?.email,
        answers: answerArray,
        notes: '', // Optional notes field
      };

      // Save to backend
      const response = await vendorRiskAssessmentAPI.create(assessmentPayload);
      console.log('Risk assessment saved successfully:', response.data);
      toast.success('Risk assessment saved');

      // Update form data with assessment ID and details
      handleChange('riskTierResult', {
        ...result,
        assessmentId: response.data.id,
      });
    } catch (error) {
      console.error('Failed to save risk assessment:', error);
      toast.error(
        error.response?.data?.detail || 'Failed to save risk assessment'
      );
    }
  }, [recordId, formData.entity_id, user, handleChange]);

  const getDDStatusBadge = () => {
    if (!latestDD) {
      return <Badge variant="outline" className="bg-gray-100 dark:bg-gray-800">No Assessment</Badge>;
    }
    const colors = {
      'pending': 'bg-gray-100 text-gray-700',
      'acceptable': 'bg-green-100 text-green-700',
      'requires_action': 'bg-yellow-100 text-yellow-700',
      'unacceptable': 'bg-red-100 text-red-700',
    };
    const labels = {
      'pending': 'Pending',
      'acceptable': 'Acceptable',
      'requires_action': 'Requires Action',
      'unacceptable': 'Unacceptable',
    };
    return (
      <Badge className={colors[latestDD.assessment_outcome]}>
        {labels[latestDD.assessment_outcome]}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className={cn("min-h-screen bg-background", theme === 'dark' ? 'dark' : '')}>
      {/* Header */}
      <div className="border-b bg-card">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center justify-between gap-y-2 gap-x-4">
            <div className="flex items-center gap-4 min-w-0">
              <Button variant="ghost" size="icon" onClick={() => navigate('/compliance/vendors')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="min-w-0">
                <h1 className="text-xl font-semibold truncate">
                  {isEditing ? 'Edit Vendor' : 'Register Vendor'}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {vendorReference && <Badge variant="outline" className="mr-2">{vendorReference}</Badge>}
                  {formData.lifecycle_stage && (
                    <Badge variant="secondary" className="mr-2">
                      {LIFECYCLE_STAGES.find(s => s.value === formData.lifecycle_stage)?.label}
                    </Badge>
                  )}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {/* Auto-save indicator */}
              <div className="text-sm text-muted-foreground">
                {autoSaveStatus === 'saving' && <span className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Saving...</span>}
                {autoSaveStatus === 'saved' && <span className="flex items-center gap-1 text-green-600"><CheckCircle className="h-3 w-3" /> Saved</span>}
              </div>
              {/* Progress */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{progressPercentage}%</span>
                <Progress value={progressPercentage} className="w-24 h-2" />
              </div>
              {/* Risk Assessment Badge */}
              {formData.riskTierResult?.tier ? (
                <Badge className={cn('text-white', getRiskTierColor(formData.riskTierResult.tier))}>
                  {formData.riskTierResult.tier.charAt(0).toUpperCase() + formData.riskTierResult.tier.slice(1)} Risk
                </Badge>
              ) : (
                <Badge className="bg-gray-500 text-white">Not Assessed</Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Due Diligence Status Card */}
        {isEditing && (
          <Card className="mb-6 border-blue-200 dark:border-blue-800">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                    <ClipboardCheck className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="font-medium">Due Diligence Assessment</p>
                    <p className="text-sm text-muted-foreground">
                      {latestDD 
                        ? `Last assessment: ${latestDD.assessment_reference} (${latestDD.assessment_date || 'No date'})`
                        : 'No due diligence assessment conducted yet'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {getDDStatusBadge()}
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate(`/compliance/due-diligence/new?vendor=${recordId}`)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {latestDD ? 'New Assessment' : 'Start Assessment'}
                  </Button>
                  {latestDD && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => navigate(`/compliance/due-diligence/${latestDD.id}`)}
                    >
                      View
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          {/* Tab Navigation */}
          <TabsList className="grid grid-cols-3 h-auto p-1">
            {VENDOR_TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="flex flex-col gap-1 py-2 px-1 text-xs sm:text-sm"
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* Tab 1: Vendor Information */}
          <TabsContent value="vendor-info" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  Vendor Information
                </CardTitle>
                <CardDescription>Basic details about the third-party vendor</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {isDPO && (
                  <div className="space-y-2">
                    <Label>Entity <span className="text-red-500">*</span></Label>
                    <Select
                      value={formData.entity_id || "select"}
                      onValueChange={(val) => handleChange('entity_id', val === "select" ? "" : val)}
                      disabled={isEditing}
                    >
                      <SelectTrigger data-testid="entity-select">
                        <SelectValue placeholder="Select entity" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="select">Select entity</SelectItem>
                        {entities.map(entity => (
                          <SelectItem key={entity.id} value={entity.id}>
                            {entity.entity_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {isEditing && (
                      <p className="text-xs text-gray-500">Entity assignment cannot be changed after creation</p>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Vendor Name <span className="text-red-500">*</span></Label>
                    <Input
                      value={formData.vendor_name}
                      onChange={(e) => handleChange('vendor_name', e.target.value)}
                      placeholder="e.g., Acme Solutions Ltd"
                      data-testid="vendor-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Trading Name</Label>
                    <Input
                      value={formData.trading_name || ''}
                      onChange={(e) => handleChange('trading_name', e.target.value)}
                      placeholder="If different from registered name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Company Registration Number</Label>
                    <Input
                      value={formData.registration_number || ''}
                      onChange={(e) => handleChange('registration_number', e.target.value)}
                      placeholder="e.g., Companies Office #"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Country of Registration</Label>
                    <Select
                      value={formData.country_of_registration}
                      onValueChange={(val) => handleChange('country_of_registration', val)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CARIBBEAN_COUNTRIES.map(c => (
                          <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Company Size</Label>
                    <Select
                      value={formData.approximate_size || "select"}
                      onValueChange={(val) => handleChange('approximate_size', val === "select" ? "" : val)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select size" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="select">Select size</SelectItem>
                        {COMPANY_SIZES.map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Services Provided <span className="text-red-500">*</span></Label>
                  <Textarea
                    value={formData.services_provided}
                    onChange={(e) => handleChange('services_provided', e.target.value)}
                    placeholder="Describe the services provided by this vendor..."
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Vendor Type <span className="text-red-500">*</span></Label>
                    <Select
                      value={formData.vendor_type}
                      onValueChange={(val) => handleChange('vendor_type', val)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="processor">Data Processor</SelectItem>
                        <SelectItem value="controller">Data Controller</SelectItem>
                        <SelectItem value="joint_controller">Joint Controller</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Service Category <span className="text-red-500">*</span></Label>
                    <Input
                      value={formData.service_category}
                      onChange={(e) => handleChange('service_category', e.target.value)}
                      placeholder="e.g., Cloud Storage, Payment Processing, Analytics"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Processing Location</Label>
                    <Input
                      value={formData.processing_location}
                      onChange={(e) => handleChange('processing_location', e.target.value)}
                      placeholder="e.g., United States, Caribbean, EU"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Criticality</Label>
                    <Select
                      value={formData.criticality}
                      onValueChange={(val) => handleChange('criticality', val)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Lifecycle Stage</Label>
                  <Select
                    value={formData.lifecycle_stage}
                    onValueChange={(val) => handleChange('lifecycle_stage', val)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LIFECYCLE_STAGES.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Primary Contact Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-primary" />
                  Primary Contact
                </CardTitle>
              </CardHeader>
              <CardContent>
                {formData.contacts && formData.contacts.length > 0 ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Contact Name</Label>
                        <Input
                          value={formData.contacts[0]?.name || ''}
                          onChange={(e) => handleNestedChange('contacts', 0, 'name', e.target.value)}
                          placeholder="Full name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Role/Title</Label>
                        <Input
                          value={formData.contacts[0]?.role || ''}
                          onChange={(e) => handleNestedChange('contacts', 0, 'role', e.target.value)}
                          placeholder="e.g., Account Manager, Technical Lead"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={formData.contacts[0]?.email || ''}
                          onChange={(e) => handleNestedChange('contacts', 0, 'email', e.target.value)}
                          placeholder="email@example.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone</Label>
                        <Input
                          value={formData.contacts[0]?.phone || ''}
                          onChange={(e) => handleNestedChange('contacts', 0, 'phone', e.target.value)}
                          placeholder="+1 876 XXX XXXX"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500">No contact information available</p>
                )}
              </CardContent>
            </Card>

            {/* Data Processing Information Card */}
            <Card>
              <CardHeader>
                <CardTitle>Data Processing Details</CardTitle>
                <CardDescription>Specify what data categories and subjects this vendor processes</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Data Categories Processed */}
                <div className="space-y-3">
                  <Label>Data Categories Processed</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {DATA_CATEGORIES.map((category) => (
                      <div key={category} className="flex items-center space-x-2">
                        <Checkbox
                          id={`category-${category}`}
                          checked={(formData.data_categories_processed || []).includes(category)}
                          onCheckedChange={(checked) => {
                            const current = formData.data_categories_processed || [];
                            const updated = checked
                              ? [...current, category]
                              : current.filter(c => c !== category);
                            handleChange('data_categories_processed', updated);
                          }}
                        />
                        <label
                          htmlFor={`category-${category}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          {category}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Data Subjects Affected */}
                <div className="space-y-3">
                  <Label>Data Subjects Affected</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {DATA_SUBJECTS.map((subject) => (
                      <div key={subject} className="flex items-center space-x-2">
                        <Checkbox
                          id={`subject-${subject}`}
                          checked={(formData.data_subjects_affected || []).includes(subject)}
                          onCheckedChange={(checked) => {
                            const current = formData.data_subjects_affected || [];
                            const updated = checked
                              ? [...current, subject]
                              : current.filter(s => s !== subject);
                            handleChange('data_subjects_affected', updated);
                          }}
                        />
                        <label
                          htmlFor={`subject-${subject}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          {subject}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* General Notes */}
            <Card>
              <CardHeader>
                <CardTitle>Additional Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Any additional notes about this vendor..."
                  rows={4}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 2: Contract */}
          <TabsContent value="contract" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  Contract Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Contract Start Date</Label>
                    <DatePicker
                      value={formData.contract_info.contract_start_date?.split('T')[0] || ''}
                      onChange={(val) => handleNestedChange('contract_info', 'contract_start_date', val)}
                      placeholder="Select contract start date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contract End Date</Label>
                    <DatePicker
                      value={formData.contract_info.contract_end_date?.split('T')[0] || ''}
                      onChange={(val) => handleNestedChange('contract_info', 'contract_end_date', val)}
                      placeholder="Select contract end date"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="auto-renewal"
                    checked={formData.contract_info.auto_renewal}
                    onCheckedChange={(val) => handleNestedChange('contract_info', 'auto_renewal', val)}
                  />
                  <label htmlFor="auto-renewal" className="text-sm cursor-pointer">
                    Auto-renewal enabled
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* DPA Status */}
            <Card>
              <CardHeader>
                <CardTitle>Data Processing Agreement (DPA)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>DPA Signed? <span className="text-red-500">*</span></Label>
                  <YesNoButtons
                    value={formData.contract_info.has_dpa}
                    onChange={(val) => handleNestedChange('contract_info', 'has_dpa', val)}
                    testIdPrefix="has-dpa"
                  />
                </div>

                {formData.contract_info.has_dpa && (
                  <div className="space-y-2">
                    <Label>DPA Signed Date</Label>
                    <Input
                      type="date"
                      value={formData.contract_info.dpa_signed_date?.split('T')[0] || ''}
                      onChange={(e) => handleNestedChange('contract_info', 'dpa_signed_date', e.target.value)}
                    />
                  </div>
                )}

                {formData.contract_info.has_dpa === false && (
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
                      <AlertTriangle className="h-5 w-5" />
                      <span className="font-medium">DPA Required</span>
                    </div>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                      A Data Processing Agreement should be in place before this vendor processes personal data.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 3: Risk & Review */}
          <TabsContent value="risk-review" className="space-y-6">
            {/* Risk Assessment Questions Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  Quick Risk Assessment
                </CardTitle>
                <CardDescription>Answer 7 simple questions (5 minutes). Numeric scoring is hidden - we only show you the results.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Progress Indicator */}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium">Quick Assessment</span>
                  {formData.riskAssessmentAnswers && (
                    <span className="text-sm text-muted-foreground">
                      {Object.values(formData.riskAssessmentAnswers).filter(a => a !== null).length}/7 answered
                    </span>
                  )}
                </div>
                <Progress
                  value={formData.riskAssessmentAnswers ? (Object.values(formData.riskAssessmentAnswers).filter(a => a !== null).length / 7) * 100 : 0}
                  className="h-2"
                />

                {/* Info Message */}
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    💡 This simple questionnaire helps us calculate an overall risk tier. Your answers are saved automatically.
                  </p>
                </div>

                {/* Risk Assessment Questions */}
                <div className="space-y-4">
                  {/* Progress Indicator */}
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium">
                      Progress: {Object.values(formData.riskAssessmentAnswers).filter(a => a !== null).length} of 7 answered
                    </span>
                    <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 transition-all duration-300"
                        style={{ width: `${(Object.values(formData.riskAssessmentAnswers).filter(a => a !== null).length / 7) * 100}%` }}
                      />
                    </div>
                  </div>

                  {/* Question Cards */}
                  <div className="space-y-4">
                    {RISK_ASSESSMENT_QUESTIONS.map((question, idx) => {
                      const isAnswered = formData.riskAssessmentAnswers[question.id] !== null;
                      return (
                        <div
                          key={question.id}
                          className={cn(
                            'p-4 border rounded-lg transition-colors',
                            isAnswered
                              ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                              : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800'
                          )}
                        >
                          <div className="flex items-start gap-3 mb-4">
                            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500 text-white text-xs font-medium flex-shrink-0">
                              {idx + 1}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-sm md:text-base">{question.question}</p>
                            </div>
                          </div>

                          {/* Radio Options */}
                          <div className="space-y-3 ml-9">
                            {question.options.map((option) => (
                              <label
                                key={option.value}
                                className="flex items-start gap-3 p-3 rounded border cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                              >
                                <input
                                  type="radio"
                                  name={question.id}
                                  value={option.value}
                                  disabled={isReadOnly}
                                  checked={formData.riskAssessmentAnswers[question.id] === option.value}
                                  onChange={(e) => {
                                    if (isReadOnly) return;
                                    const newAnswers = {
                                      ...formData.riskAssessmentAnswers,
                                      [question.id]: e.target.value,
                                    };
                                    handleChange('riskAssessmentAnswers', newAnswers);

                                    // Check if all 7 questions answered after this change
                                    const answeredCount = Object.values(newAnswers).filter(a => a !== null).length;
                                    if (answeredCount === 7) {
                                      const result = calculateRiskAssessment(newAnswers);
                                      if (result) {
                                        handleChange('riskTierResult', result);
                                        handleChange('riskAssessmentDate', new Date().toLocaleDateString());
                                        // Save assessment to backend
                                        saveRiskAssessment(newAnswers, result);
                                      }
                                    }
                                  }}
                                  className="mt-1 flex-shrink-0"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium leading-relaxed">{option.label}</p>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Completion Message */}
                  {Object.values(formData.riskAssessmentAnswers).filter(a => a !== null).length === 7 && (
                    <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <p className="text-sm text-green-800 dark:text-green-200">
                        ✓ All questions answered! Your risk assessment has been calculated below.
                      </p>
                    </div>
                  )}
                </div>

                {/* Results Display (shown when all 7 questions answered) */}
                {formData.riskTierResult && (
                  <div className="mt-6 p-4 border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div className="text-center mb-4">
                      <Badge className={cn('text-white text-lg px-6 py-3', getRiskTierColor(formData.riskTierResult.tier))}>
                        {formData.riskTierResult.tier.toUpperCase()} RISK
                      </Badge>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium mb-2">Recommended Action:</p>
                        <p className="text-sm">{formData.riskTierResult.recommendation}</p>
                      </div>
                      {formData.riskTierResult.riskDrivers && formData.riskTierResult.riskDrivers.length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-2">Risk Drivers:</p>
                          <ul className="text-sm space-y-1">
                            {formData.riskTierResult.riskDrivers.map((driver, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <span className="text-yellow-600">⚠</span>
                                <span>{driver}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground pt-2 border-t">
                        <p>Last assessed: {formData.riskAssessmentDate || 'Not yet'}</p>
                        {formData.riskTierResult.nextReviewDate && (
                          <p>Next review due: {formData.riskTierResult.nextReviewDate}</p>
                        )}
                      </div>
                    </div>
                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-4 border-t">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleReassess}
                        className="flex-1"
                      >
                        Reassess
                      </Button>
                      {assessmentHistory.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setShowHistory(true)}
                          className="flex-1"
                        >
                          View History ({assessmentHistory.length})
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Assessment History Modal/Panel */}
                {showHistory && (
                  <div className="mt-6 p-4 border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold">Assessment History</h3>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowHistory(false)}
                      >
                        ✕
                      </Button>
                    </div>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {assessmentHistory.map((assessment, idx) => (
                        <div
                          key={assessment.id}
                          className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                          onClick={() => {
                            // Load this assessment
                            setFormData(prev => ({
                              ...prev,
                              riskTierResult: {
                                tier: assessment.risk_tier,
                                averageScore: assessment.average_score,
                                recommendation: assessment.recommended_action,
                                riskDrivers: assessment.risk_drivers,
                                nextReviewDate: assessment.next_review_date,
                                assessmentId: assessment.id,
                              },
                              riskAssessmentDate: new Date(assessment.assessed_at).toLocaleDateString(),
                            }));
                            setShowHistory(false);
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className={cn('text-xs text-white', getRiskTierColor(assessment.risk_tier))}>
                                  {assessment.risk_tier.toUpperCase()}
                                </Badge>
                                <span className="text-sm font-medium">Score: {assessment.average_score}</span>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {new Date(assessment.assessed_at).toLocaleDateString()} by {assessment.assessed_by_name || assessment.assessed_by || 'Unknown'}
                              </p>
                            </div>
                            {idx === 0 && (
                              <Badge variant="outline" className="text-xs ml-2">Latest</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Navigation */}
          <div className="flex items-center justify-between pt-4 border-t">
            <Button
              variant="outline"
              onClick={goToPrevTab}
              disabled={activeTab === VENDOR_TABS[0].id}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Previous
            </Button>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/compliance/vendors')}>
                Cancel
              </Button>
              {!isReadOnly && (
                <Button variant="outline" onClick={() => handleSave(true)} disabled={saving}>
                  Save as Draft
                </Button>
              )}
              {activeTab === VENDOR_TABS[VENDOR_TABS.length - 1].id ? (
                !isReadOnly && (
                  <Button onClick={() => handleSave(false)} disabled={saving}>
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save & Close'}
                  </Button>
                )
              ) : (
                <Button onClick={goToNextTab}>
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
